import * as THREE from 'three';

function formatClockFromDayCycle(v) {
  const hourRaw = ((v * 24) + 6) % 24;
  const h = Math.floor(hourRaw);
  const m = Math.floor((hourRaw - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function createDayNightSystem({
  scene,
  hemi,
  ambientLight,
  dirLight,
  timeLabelEl,
  t,
  dayLengthSeconds = 1200
}) {
  let dayCycle = 0;
  let dayCount = 1;
  let nightState = { isNight: false, nightStrength: 0 };
  let sleepLockSeconds = 0;

  const dayColor = new THREE.Color(0x89c9ff);
  const duskColor = new THREE.Color(0xd07e4d);
  const nightColor = new THREE.Color(0x020408);

  function update(delta) {
    sleepLockSeconds = Math.max(0, sleepLockSeconds - Math.max(0, delta));
    const prev = dayCycle;
    dayCycle = (dayCycle + delta / dayLengthSeconds) % 1;
    if (dayCycle < prev) dayCount += 1;

    const angle = dayCycle * Math.PI * 2;
    const sunHeight = Math.sin(angle);
    // Wider twilight band: use a smoothstep-like ramp so dawn/dusk last longer
    const rawDaylight = THREE.MathUtils.clamp((sunHeight + 0.30) / 1.30, 0, 1);
    const daylight = rawDaylight * rawDaylight * (3 - 2 * rawDaylight); // smoothstep
    const nightStrength = THREE.MathUtils.clamp((0.28 - daylight) / 0.28, 0, 1);
    const isNight = daylight < 0.28;

    // Longer dusk/dawn colour blend: duskColor stays visible over a wider range
    const duskBlend = Math.sin(Math.min(1, daylight * 1.6) * Math.PI);
    const sky = nightColor.clone()
      .lerp(duskColor, duskBlend)
      .lerp(dayColor, daylight);

    scene.background.copy(sky);
    scene.fog.color.copy(sky);
    scene.fog.density = THREE.MathUtils.lerp(0.035, 0.0135, daylight);

    hemi.intensity = THREE.MathUtils.lerp(0.012, 0.5, daylight);
    ambientLight.intensity = THREE.MathUtils.lerp(0.018, 0.28, daylight);
    dirLight.intensity = THREE.MathUtils.lerp(0.008, 1.12, daylight);
    dirLight.position.set(Math.cos(angle) * 58, Math.max(-10, sunHeight * 66), Math.sin(angle) * 58);

    if (timeLabelEl) {
      timeLabelEl.innerText = `${t('hud.day_word', { day: dayCount })} - ${formatClockFromDayCycle(dayCycle)} ${isNight ? t('hud.night') : t('hud.day')}`;
    }

    nightState = { isNight, nightStrength };
  }

  function refreshLocale() {
    update(0);
  }

  function skipNightToMorning() {
    if (sleepLockSeconds > 0) return false;
    if (!nightState.isNight) return false;

    dayCount += 1;
    dayCycle = 0;
    sleepLockSeconds = 0.75;
    update(0);
    return true;
  }

  refreshLocale();

  return {
    update,
    refreshLocale,
    exportState() {
      return {
        dayCycle,
        dayCount,
        sleepLockSeconds
      };
    },
    importState(snapshot = null) {
      const nextCycle = Number(snapshot?.dayCycle);
      const nextDayCount = Number(snapshot?.dayCount);
      const nextSleepLock = Number(snapshot?.sleepLockSeconds);

      if (Number.isFinite(nextCycle)) {
        const wrapped = nextCycle % 1;
        dayCycle = wrapped < 0 ? wrapped + 1 : wrapped;
      }
      if (Number.isFinite(nextDayCount)) {
        dayCount = Math.max(1, Math.floor(nextDayCount));
      }
      if (Number.isFinite(nextSleepLock)) {
        sleepLockSeconds = Math.max(0, nextSleepLock);
      }
      update(0);
    },
    getNightState() {
      return nightState;
    },
    getDayCycle() {
      return dayCycle;
    },
    getDayCount() {
      return dayCount;
    },
    skipNightToMorning
  };
}
